import { 
  doc, 
  updateDoc, 
  arrayUnion, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  addDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// Gift Types
export const GIFT_TYPES = {
  ROSE: 'rose',
  BOUQUET: 'bouquet',
  GIFT_BOX: 'gift_box',
  TROPHY: 'trophy',
  GOLD_STAR: 'gold_star',
  HEART: 'heart',
  STAR: 'star'
};

// Gift Definitions
export const GIFTS = [
  {
    id: GIFT_TYPES.ROSE,
    name: 'ดอกกุหลาบ',
    icon: '🌹',
    price: 50,
    description: 'ดอกกุหลาบสวยงาม'
  },
  {
    id: GIFT_TYPES.BOUQUET,
    name: 'ช่อดอกไม้',
    icon: '💐',
    price: 100,
    description: 'ช่อดอกไม้สวยงาม'
  },
  {
    id: GIFT_TYPES.GIFT_BOX,
    name: 'ของขวัญ',
    icon: '🎁',
    price: 200,
    description: 'ของขวัญกล่องสวย'
  },
  {
    id: GIFT_TYPES.TROPHY,
    name: 'ถ้วยรางวัล',
    icon: '🏆',
    price: 500,
    description: 'ถ้วยรางวัล'
  },
  {
    id: GIFT_TYPES.GOLD_STAR,
    name: 'ดาวทอง',
    icon: '⭐',
    price: 1000,
    description: 'ดาวทอง'
  },
  {
    id: GIFT_TYPES.HEART,
    name: 'หัวใจ',
    icon: '💝',
    price: 150,
    description: 'หัวใจแสดงความรัก'
  },
  {
    id: GIFT_TYPES.STAR,
    name: 'ดาว',
    icon: '🌟',
    price: 75,
    description: 'ดาวสวยงาม'
  }
];

// Gift Contexts
export const GIFT_CONTEXTS = {
  PERSONAL: 'PERSONAL',
  SHIFT_SWAP: 'SHIFT_SWAP',
  GAMIFICATION_REWARD: 'GAMIFICATION_REWARD',
  TEAM_APPRECIATION: 'TEAM_APPRECIATION'
};

/**
 * ให้ของขวัญ (Nurse App)
 * @param {Object} giftData - ข้อมูลของขวัญ
 * @param {string} giftData.giftType - ประเภทของขวัญ
 * @param {string} giftData.fromUserId - ID ผู้ให้
 * @param {string} giftData.fromUserName - ชื่อผู้ให้
 * @param {string} giftData.fromUserRole - บทบาทผู้ให้
 * @param {string} giftData.toUserId - ID ผู้รับ
 * @param {string} giftData.toUserName - ชื่อผู้รับ
 * @param {string} giftData.toUserRole - บทบาทผู้รับ
 * @param {string} giftData.context - Context (PERSONAL, SHIFT_SWAP, etc.)
 * @param {string} giftData.teamId - Team ID (optional)
 * @param {string} giftData.relatedId - Related ID (shiftSwapId, challengeId, etc.)
 * @param {string} giftData.relatedType - Related Type (shiftSwap, challenge, etc.)
 * @param {string} giftData.message - Custom message (optional)
 * @returns {Promise<Object>} Gift object
 */
export const giveGift = async (giftData) => {
  try {
    const { 
      giftType, 
      fromUserId, 
      fromUserName, 
      fromUserRole,
      toUserId, 
      toUserName,
      toUserRole,
      context = GIFT_CONTEXTS.PERSONAL,
      teamId = null,
      relatedId = null,
      relatedType = null,
      message = null
    } = giftData;
    
    // ตรวจสอบว่าไม่ใช่การให้ของขวัญตัวเอง
    if (fromUserId === toUserId) {
      throw new Error('ไม่สามารถให้ของขวัญตัวเองได้!');
    }
    
    // ตรวจสอบ required fields - ถ้าไม่มี toUserRole ให้ดึงจาก Firestore
    if (!toUserRole && toUserId) {
      try {
        const toUserRef = doc(db, 'users', toUserId);
        const toUserSnap = await getDoc(toUserRef);
        if (toUserSnap.exists()) {
          const toUserData = toUserSnap.data();
          toUserRole = toUserData.role || toUserData.jobRole || 'MEMBER';
        } else {
          toUserRole = 'MEMBER'; // Fallback
        }
      } catch (err) {
        console.error('Error fetching toUser role in giveGift:', err);
        toUserRole = 'MEMBER'; // Fallback
      }
    }
    
    // ตรวจสอบอีกครั้งเพื่อให้แน่ใจว่าไม่เป็น undefined
    if (!toUserRole) {
      toUserRole = 'MEMBER';
    }
    
    // หา gift definition
    const giftDef = GIFTS.find(g => g.id === giftType);
    if (!giftDef) {
      throw new Error('ไม่พบประเภทของขวัญ');
    }
    
    const giftId = `gift_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const now = Timestamp.now();
    
    const gift = {
      giftId,
      teamId,
      
      // Gift Info
      giftType,
      giftName: giftDef.name,
      giftIcon: giftDef.icon,
      price: giftDef.price,
      
      // Sender & Receiver
      fromUserId,
      fromUserName,
      fromUserRole: fromUserRole || 'MEMBER',
      toUserId,
      toUserName,
      toUserRole,
      
      // Context
      context,
      relatedId,
      relatedType,
      
      // Message
      message: message || `ได้รับ${giftDef.name}จาก ${fromUserName}`,
      defaultMessage: `ได้รับ${giftDef.name}จาก ${fromUserName}`,
      
      // Exchange
      exchanged: false,
      exchangedAt: null,
      exchangeAmount: null,
      
      // Metadata
      timestamp: now,
      createdAt: now,
      updatedAt: now
    };

    // บันทึกการให้ของขวัญใน gifts collection
    await addDoc(collection(db, 'gifts'), gift);

    // บันทึกประวัติการให้ของขวัญของผู้ให้
    const fromUserRef = doc(db, 'users', fromUserId);
    const fromUserSnap = await getDoc(fromUserRef);
    
    if (fromUserSnap.exists()) {
      await updateDoc(fromUserRef, {
        giftHistory: arrayUnion({
          giftId,
          giftType,
          giftName: giftDef.name,
          giftIcon: giftDef.icon,
          price: giftDef.price,
          toUserId,
          toUserName,
          context,
          timestamp: now
        })
      });
    } else {
      // Create user document if not exists
      await updateDoc(fromUserRef, {
        giftHistory: [{
          giftId,
          giftType,
          giftName: giftDef.name,
          giftIcon: giftDef.icon,
          price: giftDef.price,
          toUserId,
          toUserName,
          context,
          timestamp: now
        }]
      }, { merge: true });
    }

    // บันทึกของขวัญที่ได้รับของผู้รับ
    const toUserRef = doc(db, 'users', toUserId);
    const toUserSnap = await getDoc(toUserRef);
    
    if (toUserSnap.exists()) {
      await updateDoc(toUserRef, {
        receivedGifts: arrayUnion({
          giftId,
          giftType,
          giftName: giftDef.name,
          giftIcon: giftDef.icon,
          price: giftDef.price,
          fromUserId,
          fromUserName,
          context,
          exchanged: false,
          timestamp: now
        })
      });
    } else {
      // Create user document if not exists
      await updateDoc(toUserRef, {
        receivedGifts: [{
          giftId,
          giftType,
          giftName: giftDef.name,
          giftIcon: giftDef.icon,
          price: giftDef.price,
          fromUserId,
          fromUserName,
          context,
          exchanged: false,
          timestamp: now
        }]
      }, { merge: true });
    }

    return gift;
  } catch (error) {
    console.error('Error giving gift:', error);
    throw error;
  }
};

/**
 * ดึงของขวัญที่ได้รับ
 * @param {string} userId - User ID
 * @param {string} teamId - Team ID (optional, filter by team)
 * @param {string} context - Context (optional, filter by context)
 * @returns {Promise<Array>} Array of received gifts
 */
export const getReceivedGifts = async (userId, teamId = null, context = null) => {
  try {
    const constraints = [
      where('toUserId', '==', userId)
    ];
    
    if (teamId) {
      constraints.push(where('teamId', '==', teamId));
    }
    
    if (context) {
      constraints.push(where('context', '==', context));
    }
    
    constraints.push(orderBy('timestamp', 'desc'));
    
    const q = query(
      collection(db, 'gifts'),
      ...constraints
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting received gifts:', error);
    throw error;
  }
};

/**
 * ดึงประวัติการให้ของขวัญ
 * @param {string} userId - User ID
 * @param {string} teamId - Team ID (optional, filter by team)
 * @param {string} context - Context (optional, filter by context)
 * @returns {Promise<Array>} Array of sent gifts
 */
export const getSentGifts = async (userId, teamId = null, context = null) => {
  try {
    const constraints = [
      where('fromUserId', '==', userId)
    ];
    
    if (teamId) {
      constraints.push(where('teamId', '==', teamId));
    }
    
    if (context) {
      constraints.push(where('context', '==', context));
    }
    
    constraints.push(orderBy('timestamp', 'desc'));
    
    const q = query(
      collection(db, 'gifts'),
      ...constraints
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting sent gifts:', error);
    throw error;
  }
};

/**
 * ดึงของขวัญทั้งหมดในทีม
 * @param {string} teamId - Team ID
 * @returns {Promise<Array>} Array of team gifts
 */
export const getTeamGifts = async (teamId) => {
  try {
    const q = query(
      collection(db, 'gifts'),
      where('teamId', '==', teamId),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    // If index error, try without orderBy
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.warn('Index not found for team gifts, using fallback query');
      try {
        const fallbackQ = query(
          collection(db, 'gifts'),
          where('teamId', '==', teamId)
        );
        const snapshot = await getDocs(fallbackQ);
        const gifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort manually
        gifts.sort((a, b) => {
          const aTime = a.timestamp?.toMillis?.() || 0;
          const bTime = b.timestamp?.toMillis?.() || 0;
          return bTime - aTime;
        });
        return gifts;
      } catch (fallbackError) {
        console.error('Error getting team gifts (fallback):', fallbackError);
        return [];
      }
    }
    console.error('Error getting team gifts:', error);
    return [];
  }
};

/**
 * คำนวณสถิติของขวัญ
 * @param {Array} gifts - Array of gifts
 * @returns {Object} Gift statistics
 */
export const calculateGiftStats = (gifts) => {
  const stats = {
    rose: 0,
    bouquet: 0,
    gift_box: 0,
    trophy: 0,
    gold_star: 0,
    heart: 0,
    star: 0,
    total: 0,
    totalGifts: gifts.length
  };

  gifts.forEach(gift => {
    if (stats.hasOwnProperty(gift.giftType)) {
      stats[gift.giftType] = (stats[gift.giftType] || 0) + 1;
    }
    stats.total += gift.price || 0;
  });

  return stats;
};

/**
 * ดึงรายการผู้รับของขวัญมากที่สุดในทีม
 * @param {string} teamId - Team ID
 * @param {number} limit - Limit
 * @returns {Promise<Array>} Array of top receivers
 */
export const getTopGiftReceivers = async (teamId, limit = 10) => {
  try {
    const gifts = await getTeamGifts(teamId);
    
    // Group by receiver
    const receiverMap = {};
    gifts.forEach(gift => {
      if (!receiverMap[gift.toUserId]) {
        receiverMap[gift.toUserId] = {
          userId: gift.toUserId,
          userName: gift.toUserName,
          gifts: [],
          totalPrice: 0
        };
      }
      receiverMap[gift.toUserId].gifts.push(gift);
      receiverMap[gift.toUserId].totalPrice += gift.price;
    });
    
    // Convert to array and sort
    const receivers = Object.values(receiverMap);
    receivers.sort((a, b) => b.totalPrice - a.totalPrice);
    
    return receivers.slice(0, limit);
  } catch (error) {
    console.error('Error getting top gift receivers:', error);
    throw error;
  }
};

/**
 * ดึงรายการผู้ให้ของขวัญมากที่สุดในทีม
 * @param {string} teamId - Team ID
 * @param {number} limit - Limit
 * @returns {Promise<Array>} Array of top givers
 */
export const getTopGiftGivers = async (teamId, limit = 10) => {
  try {
    const gifts = await getTeamGifts(teamId);
    
    // Group by sender
    const senderMap = {};
    gifts.forEach(gift => {
      if (!senderMap[gift.fromUserId]) {
        senderMap[gift.fromUserId] = {
          userId: gift.fromUserId,
          userName: gift.fromUserName,
          gifts: [],
          totalSpent: 0
        };
      }
      senderMap[gift.fromUserId].gifts.push(gift);
      senderMap[gift.fromUserId].totalSpent += gift.price;
    });
    
    // Convert to array and sort
    const senders = Object.values(senderMap);
    senders.sort((a, b) => b.totalSpent - a.totalSpent);
    
    return senders.slice(0, limit);
  } catch (error) {
    console.error('Error getting top gift givers:', error);
    throw error;
  }
};

